import React, {useEffect} from 'react';
import {Grid} from '@material-ui/core';
import {observer} from 'mobx-react-lite';
import {Approval} from '@/Core/mobx/ParameterStore/Approval';
import {Parameter} from '@/Core/mobx/ParameterStore/Parameter';
import {fieldOptions} from '@/Core/mobx/ParameterStore/PrequalXref';
import {TextBox} from '@/Core/UI';
import {SelectInputElement} from '../PreQualification/Components/SelectInputElement';

type DateOfBirthPickerProps = {
    valueChanged: (p?: Parameter & {name?: string}, value?: any, name?: string) => void;
    parameters: Approval;
    required: string | JSX.Element;
};

/**
 * Preapproval-style date of birth: Birth Month / Day / Year dropdowns plus a
 * read-only Age box, matching the layout in Questions/Basic.tsx (preapproval).
 *
 * Writes ONLY `parameters.age` — it intentionally omits the quote death-benefit
 * side effects present in Basic.tsx, since the Post App Wrap page is post-quote.
 */
const DateOfBirthPicker = observer(({parameters, valueChanged, required}: DateOfBirthPickerProps) => {
    useEffect(() => {
        const {byear, bmonth, bday} = parameters || {};
        if (!byear?.value || !bmonth?.value || !bday?.value) return;

        const bd = new Date(byear.value, bmonth.value - 1, bday.value);
        const cd = new Date();

        const yd = cd.getUTCFullYear() - bd.getUTCFullYear();
        const md = cd.getUTCMonth() - bd.getUTCMonth();
        const dd = cd.getUTCDate() - bd.getUTCDate();
        const age = Math.floor(yd + md / 12 + dd / 30 / 12);

        valueChanged(parameters.age, age, 'age');
    }, [parameters.byear?.value, parameters.bmonth?.value, parameters.bday?.value]);

    return (
        <>
            <Grid item xs={2}>
                <SelectInputElement
                    param={{
                        label: 'Birth Month',
                        options: fieldOptions.bmonth.options,
                        ...parameters.bmonth,
                        name: 'bmonth',
                    }}
                    required={required}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={2}>
                <SelectInputElement
                    param={{
                        label: 'Birth Day',
                        options: fieldOptions.bday.options,
                        ...parameters.bday,
                        name: 'bday',
                    }}
                    required={required}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={2}>
                <SelectInputElement
                    param={{
                        label: 'Birth Year',
                        options: fieldOptions.byear.options,
                        ...parameters.byear,
                        name: 'byear',
                    }}
                    required={required}
                    valueChanged={valueChanged}
                />
            </Grid>
            <Grid item xs={2}>
                <TextBox disabled value={parameters.age.value} label="Age" shrink />
            </Grid>
        </>
    );
});

export default DateOfBirthPicker;
